import React, { useEffect, useRef, useState } from 'react'
import { Box, IconButton, Typography, useTheme, Fab, Slider, Tooltip } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit'
import SettingsIcon from '@mui/icons-material/Settings'
import TextFieldsIcon from '@mui/icons-material/TextFields'

interface TeleprompterOverlayProps {
  text: string
  isOpen: boolean
  onClose: () => void
  isRecording: boolean
  teleprompterTitle: string
  waitingText: string
}

const TeleprompterOverlay: React.FC<TeleprompterOverlayProps> = ({
  text,
  isOpen,
  onClose,
  isRecording,
  teleprompterTitle,
  waitingText
}) => {
  const theme = useTheme()
  const teleprompterRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [showControls, setShowControls] = useState(true)
  const [fontSize, setFontSize] = useState(6) // rem units
  const [showSettings, setShowSettings] = useState(false)

  useEffect(() => {
    if (teleprompterRef.current) {
      teleprompterRef.current.scrollTop = teleprompterRef.current.scrollHeight
    }
  }, [text])

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  useEffect(() => {
    if (isOpen) {
      // Hide controls after 3 seconds of inactivity
      const timer = setTimeout(() => setShowControls(false), 3000)
      
      const handleMouseMove = () => {
        setShowControls(true)
        clearTimeout(timer)
        setTimeout(() => setShowControls(false), 3000)
      }

      document.addEventListener('mousemove', handleMouseMove)
      return () => {
        clearTimeout(timer)
        document.removeEventListener('mousemove', handleMouseMove)
      }
    }
  }, [isOpen])

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement && containerRef.current) {
        await containerRef.current.requestFullscreen()
      } else if (document.exitFullscreen) {
        await document.exitFullscreen()
      }
    } catch (err) {
      console.error('Error toggling fullscreen:', err)
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape' && isFullscreen) {
      document.exitFullscreen()
    } else if (event.key === 'f' || event.key === 'F') {
      toggleFullscreen()
    } else if (event.key === '+' || event.key === '=') {
      setFontSize(prev => Math.min(prev + 0.5, 12))
    } else if (event.key === '-') {
      setFontSize(prev => Math.max(prev - 0.5, 2))
    }
  }

  if (!isOpen) return null

  return (
    <Box
      ref={containerRef}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#000000',
        zIndex: 1300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: showControls ? 'auto' : 'none',
        outline: 'none'
      }}
    >
      {/* TV-Style Teleprompter Display */}
      <Box
        ref={teleprompterRef}
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: isFullscreen ? 8 : 6,
          py: isFullscreen ? 6 : 4,
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        {/* Main Text Display */}
        <Typography
          variant="h1"
          sx={{
            color: '#FFFFFF',
            fontSize: `${fontSize}rem`,
            fontWeight: 400,
            lineHeight: 1.6,
            textAlign: 'center',
            whiteSpace: 'pre-line',
            fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
            letterSpacing: '0.02em',
            textShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
            maxWidth: '90%',
            overflow: 'visible',
            // TV teleprompter style scrolling
            animation: text ? 'none' : 'none',
            transition: 'all 0.3s ease-in-out'
          }}
        >
          {text || (
            <span style={{ opacity: 0.6, fontStyle: 'italic' }}>
              {waitingText}
            </span>
          )}
          {isRecording && (
            <span
              style={{
                borderRight: '4px solid #FFFFFF',
                animation: 'blink-caret 1s step-end infinite',
                marginLeft: '8px'
              }}
            />
          )}
        </Typography>

        {/* TV Frame Effect */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
            background: `
              radial-gradient(ellipse at center, transparent 60%, rgba(0,0,0,0.8) 100%),
              linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.1) 50%, transparent 100%)
            `,
            // TV scan lines effect
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.03) 2px, rgba(255,255,255,0.03) 4px)',
              opacity: 0.2
            }
          }}
        />
      </Box>

      {/* Floating Controls */}
      {showControls && (
        <>
          {/* Top Controls */}
          <Box
            sx={{
              position: 'absolute',
              top: 20,
              right: 20,
              display: 'flex',
              gap: 2,
              opacity: showControls ? 1 : 0,
              transition: 'opacity 0.3s ease-in-out'
            }}
          >
            <Tooltip title="Settings">
              <Fab
                size="medium"
                onClick={() => setShowSettings(!showSettings)}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  color: '#FFFFFF',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)'
                  }
                }}
              >
                <SettingsIcon />
              </Fab>
            </Tooltip>

            <Tooltip title={isFullscreen ? "Exit Fullscreen (F)" : "Fullscreen (F)"}>
              <Fab
                size="medium"
                onClick={toggleFullscreen}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  color: '#FFFFFF',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)'
                  }
                }}
              >
                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </Fab>
            </Tooltip>

            <Tooltip title="Close (Esc)">
              <Fab
                size="medium"
                onClick={onClose}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  color: '#FFFFFF',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)'
                  }
                }}
              >
                <CloseIcon />
              </Fab>
            </Tooltip>
          </Box>

          {/* Settings Panel */}
          {showSettings && (
            <Box
              sx={{
                position: 'absolute',
                top: 80,
                right: 20,
                width: 300,
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                backdropFilter: 'blur(15px)',
                borderRadius: 3,
                p: 3,
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TextFieldsIcon sx={{ color: '#FFFFFF', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 500 }}>
                  Font Size
                </Typography>
              </Box>
              
              <Box sx={{ px: 1 }}>
                <Slider
                  value={fontSize}
                  onChange={(_, value) => setFontSize(value as number)}
                  min={2}
                  max={12}
                  step={0.5}
                  marks={[
                    { value: 2, label: 'Small' },
                    { value: 6, label: 'Normal' },
                    { value: 10, label: 'Large' },
                    { value: 12, label: 'XL' }
                  ]}
                  sx={{
                    color: '#FFFFFF',
                    '& .MuiSlider-mark': {
                      backgroundColor: 'rgba(255, 255, 255, 0.3)'
                    },
                    '& .MuiSlider-markLabel': {
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '0.75rem'
                    }
                  }}
                />
              </Box>
            </Box>
          )}

          {/* Bottom Info Bar */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 20,
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              backdropFilter: 'blur(10px)',
              borderRadius: 20,
              px: 3,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}
          >
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              {isRecording ? '🔴 Recording' : '⏸️ Ready'} • F: Fullscreen • +/-: Font Size • Esc: Exit
            </Typography>
          </Box>
        </>
      )}

      {/* Title Overlay - Only show when not fullscreen or when controls are visible */}
      {(!isFullscreen || showControls) && (
        <Box
          sx={{
            position: 'absolute',
            top: isFullscreen ? 80 : 20,
            left: 20,
            opacity: showControls ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out'
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontWeight: 700,
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)',
              background: 'linear-gradient(45deg, #00CFF3, #0066CC)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            {teleprompterTitle}
          </Typography>
        </Box>
      )}

      <style>
        {`
          @keyframes blink-caret {
            from, to {
              border-color: transparent;
            }
            50% {
              border-color: #FFFFFF;
            }
          }
        `}
      </style>
    </Box>
  )
}

export default TeleprompterOverlay